import type { User } from "@/entities/user";
import type { EntityServiceResponseError } from "@/services/blackfisk";

import { blackfiskAxiosRequest } from "@/services/blackfisk";

export interface UserRole {
  id: number;
  role: string;
  roleTypeId: number;
  modifiedAt: string;
  modifiedBy: number;
  createdAt: string;
  createdBy: number;
  description: string;
}
export interface UserAccount {
  id: number;
  name: string;
  ianaTimeZoneString: string;
}
export interface LoggedInUser extends User {
  roles: UserRole[];
  accounts: UserAccount[];
}
export interface SessionAuthResponse {
  accessToken: string;
  csrfToken: string;
  expiresIn: number;
  user?: LoggedInUser;
  // * Server-minted device binding — persisted by the client and echoed back as
  //   `x-device-id` on refresh/profile calls. Optional: only login/quickLogin mint it.
  deviceId?: string;
}
export interface SessionProfileResponse {
  user?: LoggedInUser;
}

export async function authLogin({ email = "", password = "" } = {}): Promise<{
  errors: EntityServiceResponseError[];
  user: LoggedInUser | null;
}> {
  if (!email || !password) {
    throw new Error("Login requires an email and password.");
  }
  const { errors, data } = await blackfiskAxiosRequest<LoggedInUser[], LoggedInUser | null>(
    {
      method: "POST",
      url: "/v1/blackfisk/user/login",
      data: {
        email,
        password,
      },
    },
    (response) => {
      return response.data?.[0] ?? null;
    },
  );
  return {
    errors,
    user: data,
  };
}
export async function pinLogin({ userId = 0, pin = 0, checksum = "" } = {}): Promise<{
  errors: EntityServiceResponseError[];
  user: LoggedInUser | null;
}> {
  if (!userId || !pin || !checksum) {
    throw new Error("Pin login requires a userId, pin, and checksum.");
  }
  const { errors, data } = await blackfiskAxiosRequest<LoggedInUser[], LoggedInUser | null>(
    {
      method: "POST",
      url: "/v1/blackfisk/user/quickLogin",
      data: {
        id: userId,
        pin,
        checksum,
      },
    },
    (response) => {
      return response.data?.[0] ?? null;
    },
  );
  return {
    errors,
    user: data,
  };
}
export async function sessionLogin({ email = "", password = "" } = {}): Promise<{
  errors: EntityServiceResponseError[];
  session: SessionAuthResponse | null;
}> {
  const { errors, data } = await blackfiskAxiosRequest<
    SessionAuthResponse[],
    SessionAuthResponse | null
  >(
    {
      method: "POST",
      url: "/v2/auth/login",
      data: {
        email,
        password,
      },
    },
    (response) => {
      return response.data?.[0] ?? null;
    },
  );
  return {
    errors,
    session: data,
  };
}
export async function sessionQuickLogin({ email = "", pin = 0, checksum = "" } = {}): Promise<{
  errors: EntityServiceResponseError[];
  session: SessionAuthResponse | null;
}> {
  const { errors, data } = await blackfiskAxiosRequest<
    SessionAuthResponse[],
    SessionAuthResponse | null
  >(
    {
      method: "POST",
      url: "/v2/auth/quickLogin",
      data: {
        email,
        pin: String(pin),
        checksum,
      },
    },
    (response) => {
      return response.data?.[0] ?? null;
    },
  );
  return {
    errors,
    session: data,
  };
}
export async function sessionRefresh(): Promise<{
  errors: EntityServiceResponseError[];
  session: SessionAuthResponse | null;
}> {
  const { errors, data } = await blackfiskAxiosRequest<
    SessionAuthResponse[],
    SessionAuthResponse | null
  >(
    {
      method: "POST",
      url: "/v2/auth/refresh",
      data: {},
    },
    (response) => {
      return response.data?.[0] ?? null;
    },
  );
  return {
    errors,
    session: data,
  };
}
export async function sessionLogout(): Promise<void> {
  await blackfiskAxiosRequest<null, null>(
    {
      method: "POST",
      url: "/v2/auth/logout",
      data: {},
    },
    () => null,
  );
}
export async function profile(): Promise<{
  errors: EntityServiceResponseError[];
  user: LoggedInUser | null;
}> {
  const { errors, data } = await blackfiskAxiosRequest<
    SessionProfileResponse[],
    LoggedInUser | null
  >(
    {
      method: "POST",
      url: "/v2/auth/profile",
      data: {},
    },
    (response) => {
      return response.data?.[0]?.user ?? null;
    },
  );
  return {
    errors,
    user: data,
  };
}
export async function quickVerify({ userId = 0, checksum = "" } = {}): Promise<{
  errors: EntityServiceResponseError[];
  user: { id: number; email: string; uuid: string } | null;
}> {
  const { errors, data } = await blackfiskAxiosRequest<
    { id: number; email: string; uuid: string }[],
    { id: number; email: string; uuid: string } | null
  >(
    {
      method: "POST",
      url: "/v1/blackfisk/user/quickVerify",
      data: {
        userId,
        checksum,
      },
    },
    (response) => {
      return response.data?.[0] ?? null;
    },
  );
  return {
    errors,
    user: data,
  };
}
